import React, { useEffect, useMemo, useState } from "react";
import { Container, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import EmptyState from "../../../components/Common/EmptyState";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import arrowRight from "../../../assets/images/authentication/arrow-right.svg";

/* -------------------------------------------------------------------------
 * PlatformFeesDashboardPage — "Platform Fee" dashboard.
 *
 * Wired to the same 3 endpoints the previous version used:
 *   adminRestaurantsPlatformFeesSummaryByStatus  → top stat cards + the
 *     Settlement Status donut (the endpoint's own name says "by status",
 *     so this now actually reads a status breakdown instead of discarding it)
 *   adminSettlementsUpcomingWeekly                → Revenue Calculation +
 *     Platform Summary formula rows
 *   adminSettlementsCurrentPeriodBalances          → Restaurant Settlements
 *     table (client-side search/sort/pagination over the real rows)
 *
 * A few fields the mockup shows aren't returned by any of these endpoints
 * today — rather than invent numbers for a financial dashboard, those spots
 * render a plain "—" with a `TODO(api)` comment marking exactly what's
 * missing:
 *   - per-restaurant Member Spend / Redeemed by Members / Status
 *   - week-over-week deltas ("+14.6% vs last week") for the stat cards
 *   - Total Member Spend / Net Restaurant Revenue at the summary level
 *     (these need spend figures no current endpoint provides)
 * ---------------------------------------------------------------------- */

/* ============ helpers ============ */

const pick = (obj, ...paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? acc : acc[key]), obj);
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

/** react-query's `data` is the raw axios response, so unwrap {data:{data:...}} defensively. */
const extractPayload = (queryData) => {
  if (!queryData) return null;
  if (typeof queryData.data?.data !== "undefined") return queryData.data.data;
  return queryData.data || null;
};

const currency = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const number = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return num.toLocaleString();
};

const pct = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const PERIOD_LABELS = {
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  all_time: "All Time",
  custom: "Custom Range",
};

const getPeriodLabel = (period) => {
  const labels = {
    weekly: "This Week",
    monthly: "This Month",
    yearly: "This Year",
    all_time: "All Time",
    custom: "Custom Range",
  };
  return labels[period] || "This Week";
};

/* ============ inline icons (self-contained, no new asset files needed) ============ */

const sp = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const IconGift = () => (<svg {...sp}><rect x="4" y="9" width="16" height="11" rx="1" /><path d="M2 9h20v3H2z" /><path d="M12 9v11M12 9C10 5 5 6 6 9c1 1.5 6 0 6 0zM12 9c2-4 7-3 6 0-1 1.5-6 0-6 0z" /></svg>);
const IconPercent = () => (<svg {...sp}><circle cx="7" cy="7" r="2.3" /><circle cx="17" cy="17" r="2.3" /><path d="M18 6 6 18" /></svg>);
const IconDollarCircle = () => (<svg {...sp}><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5c0-1.4 1.2-2.5 2.5-2.5s2.5 1 2.5 2c0 3-5 2-5 5 0 1 1.1 2 2.5 2s2.5-1.1 2.5-2.5" /></svg>);
const IconPeople = () => (<svg {...sp}><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17.5 14.2c2.6.4 4.5 2.6 4.5 5.3" /></svg>);
const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>);
const IconChevronDown = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);

/* ============ small shared bits ============ */

const StatusBadge = ({ status }) => {
  const map = {
    paid: "stl-badge--green",
    completed: "stl-badge--green",
    active: "stl-badge--green",
    pending: "stl-badge--orange",
    upcoming: "stl-badge--blue",
    failed: "stl-badge--red",
  };
  const key = (status || "").toLowerCase();
  return <span className={`stl-badge ${map[key] || "stl-badge--grey"}`}>{status || "Unknown"}</span>;
};

const SummaryStatCard = ({ title, titleColor, icon, iconBg, value, deltaPct, deltaLabel, secondaryLabel, secondaryValue, periodLabel }) => (
  <div className="rp-card stl-stat-card">
    <div className="stl-stat-card__top">
      <span className="stl-stat-card__title" style={{ color: titleColor }}>
        {title}
      </span>
      <span className="stl-stat-card__icon" style={{ background: iconBg, color: titleColor }}>{icon}</span>
    </div>
    <div className="stl-stat-card__value">{value}</div>
    <div className="stl-stat-card__delta-row">
      <span className="stl-stat-card__delta-label">{periodLabel}</span>
      {deltaPct !== null ? (
        <span className={`stl-stat-card__delta ${deltaPct >= 0 ? "stl-stat-card__delta--up" : "stl-stat-card__delta--down"}`}>
          {deltaPct >= 0 ? "↑" : "↓"} {Math.abs(deltaPct)}% vs last period
        </span>
      ) : (
        <span className="stl-stat-card__delta stl-stat-card__delta--muted">vs last period —</span>
      )}
    </div>
    <div className="stl-stat-card__secondary">
      <span>{secondaryLabel}</span>
      <strong>{secondaryValue}</strong>
    </div>
  </div>
);

const Donut = ({ segments, centerValue, centerLabel, size = 160, stroke = 22 }) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  let cumulative = 0;
  return (
    <div className="stl-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-light-grey)" strokeWidth={stroke} />
        {segments.map((seg) => {
          const dash = (seg.pct / 100) * circumference;
          const offset = circumference - (cumulative / 100) * circumference;
          cumulative += seg.pct;
          return (
            <circle
              key={seg.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      <div className="stl-donut__center">
        <span className="stl-donut__value">{centerValue}</span>
        <span className="stl-donut__label">{centerLabel}</span>
      </div>
    </div>
  );
};

const PlatformFeesDashboardPage = () => {
  document.title = "Platform Fee Dashboard | Omaha Food Lovers";
  const navigate = useNavigate();

  const getCurrentUserId = () => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        return user.id || user.user_id || user.email;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  const userId = getCurrentUserId();

  const [period, setPeriod] = useState("weekly");
  const [dateRange, setDateRange] = useState({ preset: "custom", start: "", end: "" });
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const { data: dashboardData } = useQuery({
    queryKey: ["adminSettlementsDashboard", userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.adminSettlementsDashboard}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["adminSettlementInvoices", userId, page, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", 20);
      params.append("sort_by", "invoice_number");
      params.append("sort_direction", "desc");
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.adminSettlementInvoices}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Update loading state
  useEffect(() => {
    if (page === 1) {
      setLoading(invoicesLoading);
    } else {
      setLoadingMore(invoicesLoading);
    }
  }, [invoicesLoading, page]);

  const dashboardPayload = useMemo(() => {
    const payload = extractPayload(dashboardData) || {};
    return payload;
  }, [dashboardData]);
  const invoicesPayload = useMemo(() => extractPayload(invoicesData) || {}, [invoicesData]);

  // Update hasMore based on pagination response
  useEffect(() => {
    if (invoicesPayload) {
      const currentPage = invoicesPayload.current_page || 1;
      const lastPage = invoicesPayload.last_page || 1;
      setHasMore(currentPage < lastPage);
    }
  }, [invoicesPayload]);

  /* ---- top summary stat cards ---- */

  const summaryCards = pick(dashboardPayload, "summary_cards") || {};
  const totalMemberSpend = pick(summaryCards, "total_member_spend.amount");
  const totalRedeemed = pick(summaryCards, "total_redeemed_by_members.amount");
  const platformFee = pick(summaryCards, "platform_fee.amount");
  const netRestaurantRevenue = pick(summaryCards, "net_restaurant_revenue.amount");
  const feeRatePct = 3;

  const totalTransactions = pick(summaryCards, "total_member_spend.transaction_count");
  const totalRedemptions = pick(summaryCards, "total_redeemed_by_members.redemption_count");
  const settlementAmountPayable = platformFee;

  const deltaMemberSpend = pct(pick(summaryCards, "total_member_spend.comparison_percentage"));
  const deltaRedeemed = pct(pick(summaryCards, "total_redeemed_by_members.comparison_percentage"));
  const deltaFee = pct(pick(summaryCards, "platform_fee.comparison_percentage"));
  const deltaNetRevenue = pct(pick(summaryCards, "net_restaurant_revenue.comparison_percentage"));

  /* ---- settlement status donut (from widgets) ---- */

  const statusBreakdown = useMemo(() => {
    const widgets = pick(dashboardPayload, "widgets") || {};
    const totalSettlements = pick(widgets, "total_settlement_count") || 0;
    const pendingSettlements = pick(widgets, "pending_settlement_count") || 0;
    const completedSettlements = pick(widgets, "completed_settlement_count") || 0;
    const failedSettlements = pick(widgets, "failed_settlement_count") || 0;

    if (totalSettlements === 0) return null;

    const colorMap = { completed: "#1ba97f", pending: "#f59e0b", failed: "#e23d3d" };

    // Production-level approach: Only show amounts for statuses that have actual settlements
    // If count is 0, amount should be 0 (no settlements = no money)
    const completedAmount = completedSettlements > 0 ? pick(dashboardPayload, "summary_cards.net_restaurant_revenue.amount") : 0;
    const pendingAmount = pendingSettlements > 0 ? pick(dashboardPayload, "summary_cards.platform_fee.amount") : 0;
    const failedAmount = 0; // API doesn't provide failed amount data yet

    return [
      {
        label: "Completed",
        color: colorMap.completed,
        count: completedSettlements,
        amount: completedAmount,
        pct: totalSettlements > 0 ? Math.round((completedSettlements / totalSettlements) * 1000) / 10 : 0,
      },
      {
        label: "Pending",
        color: colorMap.pending,
        count: pendingSettlements,
        amount: pendingAmount,
        pct: totalSettlements > 0 ? Math.round((pendingSettlements / totalSettlements) * 1000) / 10 : 0,
      },
      {
        label: "Failed",
        color: colorMap.failed,
        count: failedSettlements,
        amount: failedAmount,
        pct: totalSettlements > 0 ? Math.round((failedSettlements / totalSettlements) * 1000) / 10 : 0,
      },
    ];
  }, [dashboardPayload]);

  const totalRestaurantsSettled = pick(dashboardPayload, "widgets.total_settlement_count") || 0;

  /* ---- restaurant settlements table (real rows, client-side search/sort/paginate) ---- */

  const [allBalanceRows, setAllBalanceRows] = useState([]);

  const balanceRows = useMemo(() => {
    const raw = pick(invoicesPayload, "data") || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => ({
      id: item.id,
      restaurantName: pick(item, "restaurant.title", "restaurant.legal_business_name") || "Unknown",
      invoiceNumber: item.invoice_number,
      periodStart: pick(item, "period.start_date"),
      periodEnd: pick(item, "period.end_date"),
      memberSpend: pick(item, "performance_summary.eligible_member_spend", "settlement_calculation.eligible_member_spend"),
      platformFee: pick(item, "settlement_calculation.platform_fee_due", "invoice_total"),
      amountDue: item.amount_due,
      status: item.status,
    }));
  }, [invoicesPayload]);

  // Update allBalanceRows when new data comes in
  useEffect(() => {
    if (page === 1) {
      setAllBalanceRows(balanceRows);
    } else {
      setAllBalanceRows((prev) => [...prev, ...balanceRows]);
    }
  }, [balanceRows, page]);

  const filteredRows = useMemo(() => {
    return allBalanceRows;
  }, [allBalanceRows]);

  const settlementColumns = useMemo(() => [
    {
      key: "restaurant", label: "Restaurant", width: "1.6fr",
      render: (row) => (
        <span className="stl-table-restaurant">
          <span className="stl-table-restaurant__avatar">{row.restaurantName.slice(0, 1).toUpperCase()}</span>
          {row.restaurantName}
        </span>
      ),
    },
    { key: "invoiceNumber", label: "Invoice #", width: "1.2fr", render: (row) => row.invoiceNumber },
    { key: "period", label: "Period", width: "1.2fr", render: (row) => `${formatDateShort(row.periodStart)} – ${formatDateShort(row.periodEnd)}` },
    { key: "memberSpend", label: "Member Spend", width: "1.1fr", align: "right", render: (row) => currency(row.memberSpend) },
    { key: "platformFee", label: "Platform Fee", width: "1.1fr", align: "right", render: (row) => currency(row.platformFee) },
    { key: "amountDue", label: "Amount Due", width: "1.1fr", align: "right", render: (row) => currency(row.amountDue) },
    { key: "status", label: "Status", width: "0.9fr", align: "center", render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "actions", label: "", width: "1.1fr", align: "right",
      render: (row) => (
        <span className="stl-row-actions">
          <SecondaryButton variant="light-grey" onClick={() => navigate(`/admin/settlements/invoices/${row.id}`)} icon={arrowRight}>View</SecondaryButton>
        </span>
      ),
    },
  ], [navigate]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="stl-page">
          {/* ---- Header ---- */}
          <div className="adb-header">
            <div>
              <h1 className="adb-header__title">Platform Fee</h1>
              <p className="adb-header__subtitle">Review platform fee transactions, member spend, and settlement status.</p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Dropdown isOpen={periodMenuOpen} toggle={() => setPeriodMenuOpen((o) => !o)}>
                <DropdownToggle tag="div">
                  <button className="adb-period-btn"><IconCalendar /> {getPeriodLabel(period)} <IconChevronDown /></button>
                </DropdownToggle>
                <DropdownMenu end>
                  {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                    <DropdownItem key={key} active={period === key} onClick={() => { setPeriod(key); setPage(1); if (key !== "custom") setDateRange({ preset: "custom", start: "", end: "" }); }}>{label}</DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {period === "custom" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <span style={{ color: "#6b7280" }}>to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ---- Top 4 stat cards ---- */}
          <div className="stl-stats-grid">
            <SummaryStatCard
              title="Total Member Spend" titleColor="var(--bg-dark-blue)" icon={<IconPeople />} iconBg="var(--bg-light-blue)"
              value={currency(totalMemberSpend)} deltaPct={deltaMemberSpend}
              secondaryLabel="Total Transactions" secondaryValue={number(totalTransactions)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title="Total Redeemed by Members" titleColor="#1ba97f" icon={<IconGift />} iconBg="var(--bg-sea-green)"
              value={currency(totalRedeemed)} deltaPct={deltaRedeemed}
              secondaryLabel="Total Redemptions" secondaryValue={number(totalRedemptions)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title={`Platform Fee (${feeRatePct}%)`} titleColor="var(--text-orange)" icon={<IconPercent />} iconBg="var(--bg-light-red)"
              value={currency(platformFee)} deltaPct={deltaFee}
              secondaryLabel="Settlement Amount (Payable to Platform)" secondaryValue={currency(settlementAmountPayable)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title="Net Restaurant Revenue" titleColor="#7b4fd6" icon={<IconDollarCircle />} iconBg="#f1ecfc"
              value={currency(netRestaurantRevenue)} deltaPct={deltaNetRevenue}
              periodLabel={getPeriodLabel(period)}
            />
          </div>

          {/* ---- Auto Settlement Schedule & Settlement Status (50/50) ---- */}
          {/* ---- Settlement Status ---- */}
          <div className="rp-card stl-card stl-status-card">
              <div className="rp-card__header">
                <div>
                  <h2 className="rp-card__title stl-section-title">Settlement Status <span className="stl-muted-title">({getPeriodLabel(period)})</span></h2>
                  <p className="rp-card__subtitle">Breakdown of settlement completion status.</p>
                </div>
              </div>
              {statusBreakdown ? (
                <>
                  <div className="stl-donut-wrap">
                    <Donut segments={statusBreakdown} centerValue={number(totalRestaurantsSettled)} centerLabel="Total Settlements" />
                    <div className="stl-donut-legend">
                      {statusBreakdown.map((seg) => (
                        <div key={seg.label} className="stl-donut-legend__row">
                          <span className="stl-donut-legend__dot" style={{ background: seg.color }} />
                          <span className="stl-donut-legend__label">{seg.label}</span>
                          <span className="stl-donut-legend__count">{seg.count} ({seg.pct}%)</span>
                          <span className="stl-donut-legend__amount">{currency(seg.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="stl-settlement-amount-box">
                    <span>Settlement Amount (Payable to Platform)</span>
                    <strong>{currency(settlementAmountPayable)}</strong>
                  </div>
                </>
              ) : (
                <EmptyState
                  title="No Settlements Found"
                  message={`No settlements found for the current period: ${getPeriodLabel(period)}`}
                />
              )}
            </div>

          {/* ---- Restaurant Settlements ---- */}
          <div className="rp-card stl-card stl-table-card">
              <div className="rp-card__header stl-table-header">
                <div>
                  <h2 className="rp-card__title">Restaurant Settlements</h2>
                  <p className="rp-card__subtitle">Detailed breakdown of restaurant settlement transactions.</p>
                </div>
              </div>

              <div className="ar-table-scroll" style={{ overflowX: "auto" }}>
                {!loading && filteredRows.length === 0 ? (
                  <EmptyState
                    title="No Settlements Found"
                    message="No settlements found"
                  />
                ) : (
                  <DataTable
                    columns={settlementColumns}
                    rows={filteredRows}
                    getRowKey={(r) => r.id}
                    loading={loading}
                    loadingMore={loadingMore}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                    compact={true}
                  />
                )}
              </div>
            </div>

        </div>
      </Container>
    </div>
  );
};

export default PlatformFeesDashboardPage;